import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import {
  BarChart,
  Bar,
  Cell,
  LabelList,
  PieChart,
  Pie,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { NestedPanel, SectionHeader } from './Section';
import { useChartStyles, ChartTooltipCard, CategoryTick } from './chartStyles';

export interface ShareSlice {
  name: string;
  /** Share in percent (0-100). */
  value: number;
  /** The underlying count or score the share was computed from. */
  rawValue: number;
  color: string;
}

interface ShareDonutProps {
  title: string;
  data: ShareSlice[];
  /** What rawValue counts, e.g. "Posts" or "Engagement". */
  rawLabel: string;
  formatRaw?: (n: number) => string;
  /** Large number in the middle of the donut, with a caption under it. */
  centerValue?: string;
  centerCaption?: string;
  /** How many rows the list shows before collapsing the rest. */
  maxRows?: number;
}

/**
 * Donut chart with a ranked list beside it. The list replaces the old
 * floating slice labels, which were truncated and collided on small slices.
 */
export const ShareDonut: React.FC<ShareDonutProps> = ({
  title,
  data,
  rawLabel,
  formatRaw = (n) => Math.round(n).toLocaleString(),
  centerValue,
  centerCaption,
  maxRows = 10,
}) => {
  const theme = useTheme();
  const shown = data.slice(0, maxRows);
  const hidden = data.slice(maxRows);
  const hiddenShare = hidden.reduce((sum, d) => sum + d.value, 0);

  return (
    <NestedPanel sx={{ display: 'flex', flexDirection: 'column' }}>
      <SectionHeader title={title} level="sub" />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 2, md: 3 },
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          flex: 1,
        }}
      >
        <Box sx={{ position: 'relative', width: 196, height: 196, flexShrink: 0, mx: { xs: 'auto', sm: 0 } }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius="64%"
                outerRadius="100%"
                paddingAngle={data.length > 1 ? 1.2 : 0}
                dataKey="value"
                stroke={theme.ink.surfaceAlt}
                strokeWidth={1}
                isAnimationActive={false}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <RechartsTooltip
                // Float above the center number and let the card leave the small donut box
                wrapperStyle={{ zIndex: 5, pointerEvents: 'none' }}
                allowEscapeViewBox={{ x: true, y: true }}
                content={({ active, payload }: any) =>
                  active && payload && payload.length ? (
                    <ChartTooltipCard
                      title={payload[0].payload.name}
                      rows={[
                        { label: rawLabel, value: formatRaw(payload[0].payload.rawValue) },
                        { label: 'Share', value: `${payload[0].payload.value.toFixed(1)}%` },
                      ]}
                    />
                  ) : null
                }
              />
            </PieChart>
          </ResponsiveContainer>
          {centerValue && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
                zIndex: 1,
              }}
            >
              <Typography variant="h5" component="span" sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>
                {centerValue}
              </Typography>
              {centerCaption && (
                <Typography variant="caption" component="span">
                  {centerCaption}
                </Typography>
              )}
            </Box>
          )}
        </Box>

        <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0, flex: 1, minWidth: 0 }}>
          {shown.map((d) => (
            <Box
              component="li"
              key={d.name}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 0.5,
                borderBottom: 1,
                borderColor: 'divider',
                '&:last-of-type': { borderBottom: 0 },
              }}
            >
              <Box component="span" sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: d.color, flexShrink: 0 }} />
              <Typography
                variant="body2"
                component="span"
                title={d.name}
                sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.8125rem' }}
              >
                {d.name}
              </Typography>
              <Typography
                variant="body2"
                component="span"
                sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', fontSize: '0.8125rem' }}
              >
                {d.value.toFixed(1)}%
              </Typography>
            </Box>
          ))}
          {hidden.length > 0 && (
            <Typography component="li" variant="caption" sx={{ display: 'block', pt: 0.75 }}>
              {hidden.length} more, {hiddenShare.toFixed(1)}% combined
            </Typography>
          )}
        </Box>
      </Box>
    </NestedPanel>
  );
};

export interface RankBarDatum {
  name: string;
  value: number;
  color?: string;
}

interface RankBarChartProps {
  data: RankBarDatum[];
  formatValue: (n: number) => string;
  /** Axis caption under the value axis. */
  valueLabel?: string;
  /** Tooltip row label for the value. */
  tooltipLabel?: string;
  categoryWidth?: number;
  rowHeight?: number;
  maxLabelLength?: number;
}

/** Horizontal ranking bars with readable labels and the value printed at the bar end. */
export const RankBarChart: React.FC<RankBarChartProps> = ({
  data,
  formatValue,
  valueLabel,
  tooltipLabel,
  categoryWidth = 170,
  rowHeight = 34,
  maxLabelLength = 24,
}) => {
  const theme = useTheme();
  const cs = useChartStyles();

  return (
    <Box sx={{ height: Math.max(200, data.length * rowHeight + (valueLabel ? 50 : 34)) }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 0, right: 56, left: 0, bottom: 0 }} barCategoryGap={8}>
          <CartesianGrid stroke={cs.line} strokeDasharray="3 4" horizontal={false} />
          <XAxis
            {...cs.xAxis}
            height={valueLabel ? 46 : 30}
            type="number"
            domain={[0, 'auto']}
            tickFormatter={(v) => formatValue(v)}
            label={valueLabel ? cs.axisLabel(valueLabel, 0) : undefined}
          />
          <YAxis
            {...cs.categoryAxis}
            dataKey="name"
            type="category"
            tick={<CategoryTick fill={cs.categoryAxis.tick.fill} maxLength={maxLabelLength} />}
            width={categoryWidth}
            interval={0}
          />
          <RechartsTooltip
            cursor={cs.cursor}
            content={({ active, payload }: any) =>
              active && payload && payload.length ? (
                <ChartTooltipCard
                  title={payload[0].payload.name}
                  rows={[{ label: tooltipLabel || valueLabel || 'Value', value: formatValue(payload[0].value) }]}
                />
              ) : null
            }
          />
          <Bar dataKey="value" background={cs.barBackground} radius={[0, 4, 4, 0]} maxBarSize={22} isAnimationActive={false}>
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color || theme.palette.primary.main} />
            ))}
            <LabelList
              dataKey="value"
              position="right"
              formatter={(v: number) => formatValue(v)}
              style={{ fontSize: 12, fontWeight: 600, fill: theme.palette.text.primary, fontVariantNumeric: 'tabular-nums' }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

/** Compact number: 950, 1.2k, 34k, 1.5M. */
export const formatCompact = (value: number): string => {
  const n = Math.abs(value);
  if (n >= 1_000_000) return `${(value / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 10_000) return `${Math.round(value / 1000)}k`;
  if (n >= 1000) return `${(value / 1000).toFixed(1)}k`;
  return `${Math.round(value)}`;
};

/**
 * Grey, dashed stand-in for a trend chart that hasn't been configured yet.
 * Deliberately has no numbers or labels so it can't be read as real data.
 */
export const GhostTrendPlaceholder: React.FC<{ message: string; height?: number }> = ({ message, height = 240 }) => {
  const theme = useTheme();
  const faint = alpha(theme.palette.text.secondary, 0.18);
  const line = alpha(theme.palette.text.secondary, 0.45);
  // A fixed, gently rising shape: obviously illustrative, never data
  const ys = [150, 132, 140, 118, 124, 101, 112, 94, 99, 80, 88, 70, 76, 60];
  const step = 600 / (ys.length - 1);
  const pts = ys.map((y, i) => `${(i * step).toFixed(1)},${y}`);
  const linePath = `M${pts.join(' L')}`;
  const areaPath = `${linePath} L600,180 L0,180 Z`;

  return (
    <Box sx={{ position: 'relative', height, mt: 1 }}>
      <svg viewBox="0 0 600 200" preserveAspectRatio="none" width="100%" height="100%" aria-hidden="true">
        {[40, 80, 120, 160].map((y) => (
          <line key={y} x1="0" x2="600" y1={y} y2={y} stroke={faint} strokeDasharray="4 6" vectorEffect="non-scaling-stroke" />
        ))}
        <line x1="0" x2="600" y1="180" y2="180" stroke={alpha(theme.palette.text.secondary, 0.3)} vectorEffect="non-scaling-stroke" />
        <path d={areaPath} fill={alpha(theme.palette.text.secondary, 0.06)} />
        <path d={linePath} fill="none" stroke={line} strokeWidth={2} strokeDasharray="7 6" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
      </svg>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <Box
          sx={(t) => ({
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.75,
            py: 1,
            borderRadius: '999px',
            bgcolor: t.palette.background.paper,
            border: `1px solid ${t.palette.divider}`,
            boxShadow: `0 4px 14px ${alpha('#1B1526', t.palette.mode === 'dark' ? 0.4 : 0.08)}`,
            color: 'text.secondary',
            fontSize: '0.8125rem',
            fontWeight: 500,
            textAlign: 'center',
          })}
        >
          <ShowChartIcon sx={{ fontSize: 18, color: 'primary.main' }} />
          {message}
        </Box>
      </Box>
    </Box>
  );
};
